import * as React from "react";
import { useMemo } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import usePluginEntities from "hooks/usePluginEntities";
import useMarkdownConfig from "components/common/useMarkdownConfig";
import Spinner from "components/common/Spinner";
import { Link } from "components/common/router";
import Routes from "routing/Routes";
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node instanceof HTMLAnchorElement && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});
const HTML = ({ html, transformer }) => parse(html, { replace: transformer });
const useMarkdownTransformer = () => {
  const components = usePluginEntities("markdown.augment.components");
  return useMemo(() => {
    const componentMapping = Object.fromEntries(components.map(({ id, component }) => [id, component]));
    const markers = Object.keys(componentMapping).join("|");
    const componentRegexMatcher = new RegExp(`(#(?:${markers})#.+?#(?:${markers})#)`);
    const componentRegexExtractor = new RegExp(`(#(?:(${markers}))#(.+?)#(?:${markers})#)`);
    const splitByMarker = (text) => text.split(componentRegexMatcher).filter((part) => part !== "");
    const renderComponent = (element, idx) => {
      const matchesComponent = element.match(componentRegexExtractor);
      if (matchesComponent) {
        const value = matchesComponent[3];
        const Component = componentMapping[matchesComponent[2]];
        if (Component) {
          return /* @__PURE__ */ React.createElement(Component, { key: `${value}-${idx}`, value });
        }
      }
      return element;
    };
    const replaceCustomComponents = (text) => splitByMarker(text).map(renderComponent);
    return (node) => {
      if (node.type === "text" && node.data.match(componentRegexMatcher)) {
        return /* @__PURE__ */ React.createElement(React.Fragment, null, replaceCustomComponents(node.data));
      }
      return void 0;
    };
  }, [components]);
};
const mergeTransformer = (transformers) => (domNode, idx) => {
  for (const transformer of transformers) {
    const result = transformer(domNode, idx);
    if (result) {
      return result;
    }
  }
  return void 0;
};
const Augment = ({ html, transformer }) => {
  const markdownTransformer = useMarkdownTransformer();
  const replace = useMemo(
    () => transformer ? mergeTransformer([markdownTransformer, transformer]) : markdownTransformer,
    [markdownTransformer, transformer]
  );
  return parse(html, { replace });
};
const UnsupportedImageWarning = () => /* @__PURE__ */ React.createElement("span", null, "Images are not supported for security reasons. Please enable allowed sources", " ", /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.configurationsSection("Markdown") }, "here"));
const imageWarningTransformer = (domNode) => {
  if ("name" in domNode && domNode.name === "img") {
    return /* @__PURE__ */ React.createElement(UnsupportedImageWarning, null);
  }
  return void 0;
};
const Markdown = ({ augment = false, text }) => {
  const { isInitialLoading, data: markdownConfig } = useMarkdownConfig();
  const sanitizedText = DOMPurify.sanitize(text != null ? text : "", { USE_PROFILES: { html: false } });
  const html = useMemo(() => DOMPurify.sanitize(marked(sanitizedText, { async: false })), [sanitizedText]);
  const transformer = (markdownConfig == null ? void 0 : markdownConfig.allow_all_image_sources) == true || (markdownConfig == null ? void 0 : markdownConfig.allowed_image_sources) ? void 0 : imageWarningTransformer;
  const RendererComponent = augment ? Augment : HTML;
  if (isInitialLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement(RendererComponent, { html, transformer });
};
export default Markdown;
